import { useEffect, useState } from "react";
import { usePatientJourney, PatientJourneyStep } from "@/hooks/usePatientJourney";
import { patientJourneyApi, settingsApi } from "@/integrations/mysql/client";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Trash2, Edit2, Plus, Save, X, GripVertical,
  Calendar, Target, Users, TrendingUp, CheckCircle,
  Heart, Award, Activity, Star, Zap, LucideIcon,
} from "lucide-react";
import { PageVisibilityToggle } from "@/components/ui/PageVisibilityToggle";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const ICON_MAP: Record<string, LucideIcon> = {
  Calendar, Target, Users, TrendingUp, CheckCircle,
  Heart, Award, Activity, Star, Zap,
};

const ICON_OPTIONS = Object.keys(ICON_MAP);

// ─── Sortable item ─────────────────────────────────────────────────────────

interface SortableStepProps {
  step: PatientJourneyStep;
  onEdit: (step: PatientJourneyStep) => void;
  onDelete: (id: string) => void;
  onToggleActive: (step: PatientJourneyStep) => void;
}

function SortableStep({ step, onEdit, onDelete, onToggleActive }: SortableStepProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });

  return (
    <Card
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Drag handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing mt-0.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <GripVertical className="w-5 h-5" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg bg-brand-primary/8 flex items-center justify-center shrink-0">
                {ICON_MAP[step.icon] ? (() => { const Icon = ICON_MAP[step.icon]; return <Icon className="w-4 h-4 text-brand-primary" />; })() : null}
              </div>
              <h4 className="font-semibold text-sm text-gray-800 truncate">{step.title}</h4>
              {!step.is_active && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded shrink-0">
                  Inativo
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 leading-relaxed">{step.description}</p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            <Switch
              checked={!!step.is_active}
              onCheckedChange={() => onToggleActive(step)}
            />
            <Button size="sm" variant="outline" onClick={() => onEdit(step)} className="h-8 w-8 p-0">
              <Edit2 className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('Tem certeza que deseja deletar este passo?')) onDelete(step.id);
              }}
              className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:border-red-300"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

export function PatientJourneyManager() {
  const { steps, isLoading, updateStep, deleteStep } = usePatientJourney();
  const queryClient = useQueryClient();
  const [localSteps, setLocalSteps] = useState<PatientJourneyStep[] | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<PatientJourneyStep>>({
    icon: 'Calendar', title: '', description: '', is_active: true, display_order: 0,
  });
  const [ctaLinks, setCtaLinks] = useState<Record<string, { label: string; url: string }>>({});
  const [ctaLabel, setCtaLabel] = useState('Veja aqui uma avaliação de caso');
  const [ctaUrl, setCtaUrl] = useState('');
  const [savingStep, setSavingStep] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    settingsApi.list()
      .then(settings => {
        const raw = settings.find(setting => setting.key === 'patient_journey_cta_links')?.value;
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) setCtaLinks(parsed);
          } catch { /* Ignore invalid settings and leave links empty. */ }
        }
      })
      .catch(() => {});
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Use localSteps during drag to avoid re-render flicker, fall back to hook data
  const displayedSteps = localSteps ?? steps;

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = displayedSteps.findIndex(s => s.id === active.id);
    const newIndex = displayedSteps.findIndex(s => s.id === over.id);
    const reordered = arrayMove(displayedSteps, oldIndex, newIndex);

    setLocalSteps(reordered);

    try {
      await Promise.all(reordered.map((s, i) => patientJourneyApi.update(s.id, { display_order: i })));
      toast({ title: "Ordem atualizada com sucesso!" });
    } catch {
      toast({ title: "Erro ao atualizar ordem", variant: "destructive" });
      setLocalSteps(null);
    }
  };

  const handleEdit = (step: PatientJourneyStep) => {
    setEditingId(step.id);
    setFormData(step);
    setCtaLabel(ctaLinks[step.id]?.label || 'Veja aqui uma avaliação de caso');
    setCtaUrl(ctaLinks[step.id]?.url || '');
    setIsCreating(false);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({
      icon: 'Calendar', title: '', description: '', is_active: true,
      display_order: displayedSteps.length,
    });
    setCtaLabel('Veja aqui uma avaliação de caso');
    setCtaUrl('');
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description) return;
    const normalizedUrl = ctaUrl.trim();
    if (normalizedUrl && !(/^https?:\/\//i.test(normalizedUrl) || normalizedUrl.startsWith('/'))) {
      toast({ title: 'Link inválido', description: 'Use um link https:// ou um caminho do site começando com /. ', variant: 'destructive' });
      return;
    }

    setSavingStep(true);
    try {
      const stepData = {
        icon: formData.icon || 'Calendar',
        title: formData.title,
        description: formData.description,
        is_active: formData.is_active ?? true,
        display_order: formData.display_order ?? displayedSteps.length,
      };
      const savedStep = isCreating
        ? await patientJourneyApi.create(stepData)
        : editingId
          ? await patientJourneyApi.update(editingId, stepData)
          : null;

      if (!savedStep) return;

      const nextLinks = { ...ctaLinks };
      if (normalizedUrl) {
        nextLinks[savedStep.id] = {
          label: ctaLabel.trim() || 'Veja aqui uma avaliação de caso',
          url: normalizedUrl,
        };
      } else {
        delete nextLinks[savedStep.id];
      }
      await settingsApi.update('patient_journey_cta_links', JSON.stringify(nextLinks));
      setCtaLinks(nextLinks);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['patient-journey-steps'] }),
        queryClient.invalidateQueries({ queryKey: ['site-settings'] }),
      ]);
      toast({ title: 'Passo da jornada salvo!' });
      setIsCreating(false);
      setEditingId(null);
      setFormData({ icon: 'Calendar', title: '', description: '', is_active: true, display_order: 0 });
      setCtaLabel('Veja aqui uma avaliação de caso');
      setCtaUrl('');
      setLocalSteps(null);
    } catch (error) {
      toast({ title: 'Erro ao salvar passo', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    } finally {
      setSavingStep(false);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsCreating(false);
    setFormData({ icon: 'Calendar', title: '', description: '', is_active: true, display_order: 0 });
    setCtaLabel('Veja aqui uma avaliação de caso');
    setCtaUrl('');
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageVisibilityToggle pageKey="page_jornada_visible" label="Seção Jornada do Paciente" />

      <div className="flex justify-end">
        <Button onClick={handleCreate} disabled={isCreating || editingId !== null}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Passo
        </Button>
      </div>

      {/* Form */}
      {(isCreating || editingId) && (
        <Card className="border-2 border-brand-primary/30 bg-brand-primary/3">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{isCreating ? 'Novo Passo' : 'Editar Passo'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Ícone</Label>
                <Select
                  value={formData.icon}
                  onValueChange={(v) => setFormData({ ...formData, icon: v })}
                >
                  <SelectTrigger className="mt-1">
                    <div className="flex items-center gap-2">
                      {formData.icon && ICON_MAP[formData.icon] && (() => {
                        const Icon = ICON_MAP[formData.icon!];
                        return <Icon className="w-4 h-4 text-brand-primary" />;
                      })()}
                      <span>{formData.icon}</span>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((icon) => {
                      const Icon = ICON_MAP[icon];
                      return (
                        <SelectItem key={icon} value={icon}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-brand-primary" />
                            <span>{icon}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!formData.is_active}
                    onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                  />
                  <Label className="text-sm">Visível no site</Label>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Título <span className="text-gray-400 normal-case font-normal">({formData.title?.length || 0}/100)</span>
              </Label>
              <Input
                className="mt-1"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                maxLength={100}
                placeholder="Ex: Primeira Consulta"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Descrição <span className="text-gray-400 normal-case font-normal">({formData.description?.length || 0}/300)</span>
              </Label>
              <Textarea
                className="mt-1"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={300}
                rows={3}
                placeholder="Ex: Avaliação completa do seu histórico alimentar"
              />
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Botão/link opcional</Label>
                <p className="text-xs text-gray-400 mt-1">Adicione o link da avaliação de caso ou de outro material para aparecer dentro deste passo.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Texto do botão</Label>
                  <Input className="mt-1" value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} maxLength={80} placeholder="Veja aqui uma avaliação de caso" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Link do botão</Label>
                  <Input className="mt-1" value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} maxLength={500} placeholder="https://... ou /avaliacoes" />
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <Button onClick={handleSave} disabled={savingStep || !formData.title || !formData.description}>
                <Save className="w-4 h-4 mr-2" />
                {savingStep ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button onClick={handleCancel} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sortable list */}
      {displayedSteps.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          Nenhum passo cadastrado. Adicione o primeiro!
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 flex items-center gap-1.5">
            <GripVertical className="w-3.5 h-3.5" />
            Arraste os itens para reordenar
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={displayedSteps.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {displayedSteps.map((step) => (
                <SortableStep
                  key={step.id}
                  step={step}
                  onEdit={handleEdit}
                  onDelete={(id) => deleteStep(id)}
                  onToggleActive={(s) => updateStep({ id: s.id, updates: { is_active: !s.is_active } })}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}
